import React, { useMemo } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Theme,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useTranslate, SelectInput, Translate } from "react-admin";
import { ProtectedFieldLevel } from "../../../../common/data/service-proxies";
import { useTheme } from "@material-ui/styles";

interface FieldPermissionsGroupTabProps {
  group: { [key: string]: any };
  groupName: string;
}

const getAccessLevelChoices = (accessLevels?: ProtectedFieldLevel[] | null) => {
  return (
    accessLevels?.map((x) => ({
      id: x,
      name: `permissions.fieldAccessLevel.${x}`,
    })) ?? []
  );
};

const getAccordion = (
  group: { [key: string]: any },
  permissionGroupName: string,
  groupName: string,
  translate: Translate,
  theme: Theme,
  rest: any
) => {
  const permissionGroup = group[permissionGroupName];
  const permissionNames = Object.getOwnPropertyNames(permissionGroup);
  if (!permissionNames.length) {
    return null;
  }

  return (
    <Accordion key={permissionGroupName}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-label="expand"
        aria-controls={`permission-group-${permissionGroupName}-content`}
        id={`permission-group-${permissionGroupName}-header`}>
        <Typography
          variant="body2"
          style={{ color: theme.palette.primary.main }}>
          {translate(
            `permissions.fieldPermissions.${groupName}.${permissionGroupName}`
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        id={`permission-group-${permissionGroupName}-content`}
        style={{ flexDirection: "column" }}
        className="ra-input form-accordion">
        {getInputs(permissionGroup, groupName, permissionGroupName, rest)}
      </AccordionDetails>
    </Accordion>
  );
};

const getInputs = (
  permissionGroup: { [key: string]: any },
  groupName: string,
  permissionGroupName: string,
  props: any
) => {
  const inputNames = Object.getOwnPropertyNames(permissionGroup);
  const { isEdit, ...rest } = props;
  return inputNames.map((permissionName) => {
    return (
      <SelectInput
        {...rest}
        key={permissionName}
        source={`fieldPermissions.${groupName}.${permissionGroupName}.${permissionName}.accessLevel`}
        label={`permissions.fieldPermissions.${groupName}.fields.${permissionName}`}
        choices={getAccessLevelChoices(
          permissionGroup[permissionName].FieldAccessLevels
        )}
        defaultValue={rest.isEdit ? undefined : ProtectedFieldLevel.Full}
      />
    );
  });
};

export const FieldPermissionsGroupTab = ({
  group,
  groupName,
  ...rest
}: FieldPermissionsGroupTabProps) => {
  const translate = useTranslate();
  const theme: Theme = useTheme();

  const accordions = useMemo(() => {
    const permissionGroups = Object.getOwnPropertyNames(group);
    if (!permissionGroups.length) {
      return null;
    }

    return permissionGroups.map((permissionGroupName) =>
      getAccordion(
        group,
        permissionGroupName,
        groupName,
        translate,
        theme,
        rest
      )
    );
  }, [group, groupName, translate, theme, rest]);

  return <Box>{accordions}</Box>;
};
